import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  DollarSign, Cpu, Activity, TrendingDown, BarChart3,
  Layers, Gauge, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCostDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");
  const costChartRef = useRef(null);
  const modelChartRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/analytics/admin/costs?period=${period}`);
        setData(res.data);
      } catch { /* auth redirect */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [period]);

  // Draw charts
  useEffect(() => {
    if (!data) return;
    drawCostTrendChart(costChartRef.current, data.daily_costs, data.is_hourly);
    drawModelBarChart(modelChartRef.current, data.model_breakdown);
  }, [data]);

  if (loading && !data) {
    return <div className="analytics-loading" data-testid="cost-dashboard-loading"><div className="processing-spinner" /></div>;
  }

  if (!data) {
    return <div className="analytics-loading">Failed to load cost data</div>;
  }

  const { summary, model_breakdown, step_breakdown, mode_breakdown } = data;

  return (
    <div className="page-container fade-in" data-testid="admin-cost-dashboard">
      <div className="page-header">
        <div>
          <h1 data-testid="cost-dashboard-title">Cost & Budget</h1>
          <p className="page-subtitle">API costs, efficiency metrics, and budget tracking.</p>
        </div>
        <div className="analytics-period-select" data-testid="cost-period-selector">
          {["6h", "1d", "7d", "14d", "30d", "60d", "90d"].map(p => (
            <button
              key={p}
              className={`analytics-period-btn ${period === p ? "active" : ""}`}
              onClick={() => setPeriod(p)}
              data-testid={`cost-period-${p}`}
            >{p}</button>
          ))}
        </div>
      </div>

      {/* Cost Summary Cards */}
      <div className="analytics-cost-hero" data-testid="cost-hero">
        <div className="analytics-cost-hero-card" data-testid="cost-today">
          <div className="analytics-cost-hero-label">Today</div>
          <div className="analytics-cost-hero-value">${summary.cost_today.toFixed(4)}</div>
          <div className="analytics-cost-hero-sub">{summary.resumes_today} resumes</div>
        </div>
        <div className="analytics-cost-hero-divider" />
        <div className="analytics-cost-hero-card" data-testid="cost-week">
          <div className="analytics-cost-hero-label">This Week</div>
          <div className="analytics-cost-hero-value">${summary.cost_this_week.toFixed(4)}</div>
          <div className="analytics-cost-hero-sub">{summary.resumes_this_week} resumes</div>
        </div>
        <div className="analytics-cost-hero-divider" />
        <div className="analytics-cost-hero-card" data-testid="cost-month">
          <div className="analytics-cost-hero-label">This Month</div>
          <div className="analytics-cost-hero-value">${summary.cost_this_month.toFixed(4)}</div>
          <div className="analytics-cost-hero-sub">{summary.resumes_this_month} resumes</div>
        </div>
        <div className="analytics-cost-hero-divider" />
        <div className="analytics-cost-hero-card" data-testid="cost-all-time">
          <div className="analytics-cost-hero-label">All Time</div>
          <div className="analytics-cost-hero-value">${summary.all_time_cost_usd.toFixed(4)}</div>
          <div className="analytics-cost-hero-sub">{summary.all_time_resumes} resumes</div>
        </div>
      </div>

      {/* Efficiency Metrics */}
      <div className="analytics-stats-grid" data-testid="efficiency-metrics">
        <div className="analytics-card" data-testid="metric-avg-cost">
          <div className="analytics-card-icon"><DollarSign size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Avg Cost / Resume</span>
            <span className="analytics-card-value">${summary.avg_cost_per_resume.toFixed(4)}</span>
          </div>
        </div>
        <div className="analytics-card" data-testid="metric-avg-calls">
          <div className="analytics-card-icon"><Cpu size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Avg API Calls / Resume</span>
            <span className="analytics-card-value">{summary.avg_calls_per_resume}</span>
          </div>
        </div>
        <div className="analytics-card" data-testid="metric-resumes-per-dollar">
          <div className="analytics-card-icon"><Gauge size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Resumes / Dollar</span>
            <span className="analytics-card-value" style={{ color: "var(--success)" }}>{summary.resumes_per_dollar}</span>
          </div>
        </div>
        <div className="analytics-card" data-testid="metric-avg-tokens">
          <div className="analytics-card-icon"><Activity size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Avg Tokens / Resume</span>
            <span className="analytics-card-value">{summary.avg_tokens_per_resume ? `${(summary.avg_tokens_per_resume / 1000).toFixed(1)}k` : "—"}</span>
          </div>
        </div>
      </div>

      {/* Token Summary Bar */}
      {summary.total_tokens > 0 && (
        <div className="analytics-token-summary" data-testid="token-summary">
          <div className="analytics-token-bar">
            <div className="analytics-token-segment analytics-token-input"
              style={{ width: `${(summary.total_input_tokens / summary.total_tokens * 100).toFixed(1)}%` }}>
              <span>Input: {(summary.total_input_tokens / 1000).toFixed(1)}k</span>
            </div>
            <div className="analytics-token-segment analytics-token-output"
              style={{ width: `${(summary.total_output_tokens / summary.total_tokens * 100).toFixed(1)}%` }}>
              <span>Output: {(summary.total_output_tokens / 1000).toFixed(1)}k</span>
            </div>
          </div>
          <div className="analytics-token-legend">
            <span><span className="analytics-dot" style={{ background: "#3b82f6" }} /> Input Tokens ({(summary.total_input_tokens / summary.total_tokens * 100).toFixed(0)}%)</span>
            <span><span className="analytics-dot" style={{ background: "#ef4444" }} /> Output Tokens ({(summary.total_output_tokens / summary.total_tokens * 100).toFixed(0)}%)</span>
            <span>Total: {(summary.total_tokens / 1000).toFixed(1)}k tokens</span>
          </div>
        </div>
      )}

      {/* Cost Trend Chart */}
      <div className="analytics-chart-section" data-testid="cost-trend-section">
        <div className="analytics-chart-header">
          <h2><TrendingDown size={18} /> {data.is_hourly ? "Hourly" : "Daily"} Cost Trend</h2>
        </div>
        <div className="analytics-chart-wrap" data-testid="cost-trend-chart">
          <canvas ref={costChartRef} className="analytics-canvas" />
        </div>
      </div>

      {/* Two-column: Model + Step breakdown */}
      <div className="analytics-charts-two-col" data-testid="cost-breakdowns">
        {/* Model Breakdown */}
        <div className="analytics-chart-section" data-testid="model-breakdown-section">
          <div className="analytics-chart-header">
            <h2><Layers size={18} /> Cost by Model</h2>
          </div>
          <div className="analytics-chart-wrap analytics-chart-small" data-testid="model-chart">
            <canvas ref={modelChartRef} className="analytics-canvas" />
          </div>
          <div className="analytics-table-wrap" data-testid="model-table">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Calls</th>
                  <th>Cost</th>
                  <th>Input Tok</th>
                  <th>Output Tok</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(model_breakdown).sort((a, b) => b[1].cost - a[1].cost).map(([model, info]) => (
                  <tr key={model}>
                    <td className="analytics-model-name">{model}</td>
                    <td>{info.calls}</td>
                    <td>${info.cost.toFixed(4)}</td>
                    <td>{(info.input_tokens / 1000).toFixed(1)}k</td>
                    <td>{(info.output_tokens / 1000).toFixed(1)}k</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Step Breakdown */}
        <div className="analytics-chart-section" data-testid="step-breakdown-section">
          <div className="analytics-chart-header">
            <h2><BarChart3 size={18} /> Cost by Pipeline Step</h2>
          </div>
          <div className="analytics-table-wrap" data-testid="step-table">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Calls</th>
                  <th>Cost</th>
                  <th>Tokens</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(step_breakdown).sort((a, b) => b[1].cost - a[1].cost).map(([step, info]) => (
                  <tr key={step}>
                    <td className="analytics-step-name">{formatStepName(step)}</td>
                    <td>{info.calls}</td>
                    <td>${info.cost.toFixed(4)}</td>
                    <td>{((info.input_tokens + info.output_tokens) / 1000).toFixed(1)}k</td>
                    <td>{info.time.toFixed(1)}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Processing Mode */}
          {Object.keys(mode_breakdown).length > 0 && (
            <>
              <div className="analytics-chart-header" style={{ marginTop: "1.5rem" }}>
                <h2><Clock size={18} /> By Processing Mode</h2>
              </div>
              <div className="analytics-mode-grid" data-testid="mode-breakdown">
                {Object.entries(mode_breakdown).map(([mode, info]) => (
                  <div key={mode} className="analytics-mode-card">
                    <div className="analytics-mode-name">{mode === "standard" ? "Standard" : "Flex (Economy)"}</div>
                    <div className="analytics-mode-stat">
                      <span>{info.resumes} resumes</span>
                      <span>${info.cost.toFixed(4)}</span>
                    </div>
                    {info.resumes > 0 && (
                      <div className="analytics-mode-avg">
                        ${(info.cost / info.resumes).toFixed(4)} / resume
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatStepName(step) {
  return step
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

// ── Chart helpers ──
const MODEL_COLORS = ["#6366f1", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

function getChartStyles() {
  const isDark = getComputedStyle(document.documentElement).getPropertyValue("--bg-deep").trim().startsWith("#0");
  return {
    gridColor: isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)",
    textColor: isDark ? "#64748b" : "#94a3b8",
  };
}

function drawCostTrendChart(canvas, data, isHourly) {
  if (!canvas || !data || data.length === 0) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);
  const w = rect.width;
  const h = rect.height;
  const pad = { top: 16, right: 12, bottom: 32, left: 50 };
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const { gridColor, textColor } = getChartStyles();

  ctx.clearRect(0, 0, w, h);

  const values = data.map(d => d.cost_usd || 0);
  const maxVal = Math.max(...values, 0.001);

  // Y gridlines
  for (let i = 0; i <= 4; i++) {
    const val = (maxVal / 4) * i;
    const y = pad.top + chartH - (val / maxVal) * chartH;
    ctx.fillStyle = textColor;
    ctx.font = "10px Inter, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(`$${val.toFixed(3)}`, pad.left - 6, y + 3);
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(w - pad.right, y);
    ctx.stroke();
  }

  // Stacked bars (cost + resume count overlay)
  const barGap = Math.max(1, Math.floor(chartW / data.length * 0.2));
  const barW = Math.max(2, (chartW / data.length) - barGap);

  data.forEach((d, i) => {
    const val = d.cost_usd || 0;
    const barH = maxVal > 0 ? (val / maxVal) * chartH : 0;
    const x = pad.left + i * (barW + barGap) + barGap / 2;
    const y = pad.top + chartH - barH;

    const radius = Math.min(3, barW / 2);
    ctx.beginPath();
    ctx.moveTo(x, pad.top + chartH);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.lineTo(x + barW - radius, y);
    ctx.quadraticCurveTo(x + barW, y, x + barW, y + radius);
    ctx.lineTo(x + barW, pad.top + chartH);
    ctx.closePath();
    ctx.fillStyle = val > 0 ? "#ef4444" : "rgba(148,163,184,0.1)";
    ctx.globalAlpha = 0.7;
    ctx.fill();
    ctx.globalAlpha = 1;

    const showEvery = Math.ceil(data.length / 8);
    if (i % showEvery === 0 || i === data.length - 1) {
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.font = "9px Inter, sans-serif";
      const label = isHourly
        ? (d.date || "").slice(11, 16)   // "HH:00"
        : (d.date || "").slice(5);       // "MM-DD"
      ctx.fillText(label, x + barW / 2, h - 8);
    }
  });
}

function drawModelBarChart(canvas, modelBreakdown) {
  if (!canvas || !modelBreakdown) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);
  const w = rect.width;
  const h = rect.height;

  ctx.clearRect(0, 0, w, h);

  const entries = Object.entries(modelBreakdown).sort((a, b) => b[1].cost - a[1].cost);
  if (entries.length === 0) {
    ctx.fillStyle = "#64748b";
    ctx.font = "12px Inter, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("No data", w / 2, h / 2);
    return;
  }

  const total = entries.reduce((s, [, v]) => s + v.cost, 0);
  if (total === 0) return;

  // Horizontal stacked bar
  const barY = h / 2 - 16;
  const barH = 32;
  let currentX = 8;
  const barW = w - 16;

  entries.forEach(([, info], i) => {
    const segW = (info.cost / total) * barW;
    ctx.fillStyle = MODEL_COLORS[i % MODEL_COLORS.length];
    ctx.beginPath();
    if (i === 0) {
      ctx.roundRect(currentX, barY, segW, barH, [6, 0, 0, 6]);
    } else if (i === entries.length - 1) {
      ctx.roundRect(currentX, barY, segW, barH, [0, 6, 6, 0]);
    } else {
      ctx.rect(currentX, barY, segW, barH);
    }
    ctx.fill();
    currentX += segW;
  });

  // Labels below
  const { textColor } = getChartStyles();
  let labelX = 8;
  entries.forEach(([model, info], i) => {
    const pct = Math.round((info.cost / total) * 100);
    if (pct < 3) return;
    const segW = (info.cost / total) * barW;
    ctx.fillStyle = textColor;
    ctx.font = "9px Inter, sans-serif";
    ctx.textAlign = "center";
    const shortModel = model.length > 15 ? model.slice(0, 14) + "..." : model;
    ctx.fillText(`${shortModel} (${pct}%)`, labelX + segW / 2, barY + barH + 14);
    labelX += segW;
  });
}
